import React, { useEffect, useState, useCallback } from "react";
import "@sia/style-guide/dist/base.css";
import { Route, Switch, useHistory, Redirect } from "react-router-dom";
import keycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import NotFoundPage from "./pages/Error/NotFoundPage";
import GlobalContext from "contexts/GlobalContext";
import { useTranslation } from "react-i18next";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import {
  Body,
  Footer,
  Layout,
  Loader,
  usePrivacyPolicy,
  User,
  httpClient,
  isGoogleBot,
  init as initI18n,
  getBrowserLanguageKey,
  useTranslator,
  getLocale,
  usePathFactory,
  memberTypeRepository,
  fetchParameters,
  checkMaintenancePage,
  getDisableFlags,
  checkVersion,
  WrongVersionModal,
  MaintenancePage,
} from "@sia/style-guide";
import { useFooterContents } from "./common/Layout/FooterContents";
import { Helmet, HelmetProvider } from "react-helmet-async";
import ErrorPage from "./pages/Error/ErrorPage";
import HttpErrorContextProvider from "contexts/HttpErrorContext";
import CMSContent from "pages/CMSContent/CMSContent";
import cmsAdapter from "./common/Adapters/CMSAdapter/CMSAdapter";
import CMSSearch from "pages/CMSContent/CMSSearch";
import WizardProvider from "contexts/WizardContext";
import CookieClient from "./components/CookieClient";
import UnsubscribeFromNewsletter from "pages/Newsletter/UnsubscribePage";
import HeaderClient from "components/HeaderClient";
import Catalog from "pages/Catalog/Catalog";
import Agenda from "pages/Agenda/Agenda";
import AgendaAppPage from "pages/Agenda/AgendaAppPage";

initI18n();

const MINUTES_BETWEEN_PARAMETER_CHECKS = process.env.REACT_APP_MINUTES_BETWEEN_PARAMETER_CHECKS || 5;

function App() {
  const { i18n } = useTranslation();
  const translate = useTranslator();
  const history = useHistory();
  const pathFactory = usePathFactory();
  const [isLoaderShown, setIsLoaderShown] = useState(false);
  httpClient.setLoaderStateCallback(setIsLoaderShown);
  const [menuItems, setMenuItems] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [noMenuPages, setNoMenuPages] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [isLoadingPages, setIsLoadingPages] = useState(process.env.REACT_APP_CMS_URL ? true : false);
  const [organizationLanguages, setOrganizationLanguages] = useState(process.env.REACT_APP_CMS_URL ? [] : undefined);
  const [organisationData, setOrganisationData] = useState({});
  const [user, setUser] = useState(null);
  const [headerUser, setHeaderUser] = useState(null);
  const [isIDPInitialized, setIsIDPInitialized] = useState(false);
  const [wasIDPInitializationStarted, setWasIDPInitializationStarted] = useState(false);
  const [language, setLanguage] = useState(null);
  const footerContents = useFooterContents(language);
  const [isSubscribedToAnyService, setIsSubscribedToAnyService] = useState(false);
  const [organizationCode, setOrganizationCode] = useState(null);
  const localStorageLanguageKey = localStorage.getItem("sia.webPlatform.languageKey");
  const [isCorrectVersion, setIsCorrectVersion] = useState(null);
  const [disableFlags, setDisableFlags] = useState([]);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(null);

  const globalContextValue = {
    isLoaderShown,
    setIsLoaderShown,
    isIDPInitialized: isIDPInitialized,
    user: user,
    language,
    isSubscribedToAnyService,
    menuItems,
    noMenuPages,
    organizationCode,
    organisationData,
    disableFlags,
  };

  const { PrivacyPolicyComponent, handlePrivacyPolicyNotification } = usePrivacyPolicy({
    webPlatformAdapter,
    getLocale,
    translate,
    pathFactory,
    linkUrl: `${process.env.REACT_APP_WEBSITE_URL}${pathFactory.create("privacy-policy")}`,
  });

  const defaultAppSwitcherValue = isGoogleBot(navigator.userAgent);
  const getUrlLanguageKey = useCallback(() => {
    const pathSplit = history.location.pathname.split("/");
    return pathSplit[1];
  }, [history.location.pathname]);

  const isValidLanguageKey = useCallback((languageKey) => organizationLanguages.includes(languageKey), [organizationLanguages]);

  const getApplyingLanguage = useCallback(() => {
    const urlLanguageKey = getUrlLanguageKey();
    const validUrlLanguageKey = isValidLanguageKey(urlLanguageKey) ? urlLanguageKey : null;
    if (validUrlLanguageKey) return validUrlLanguageKey;
    if (localStorageLanguageKey && isValidLanguageKey(localStorageLanguageKey)) {
      return localStorageLanguageKey;
    }
    const browserLang = getBrowserLanguageKey();
    return isValidLanguageKey(browserLang) ? browserLang : organizationLanguages[0];
  }, [localStorageLanguageKey, getUrlLanguageKey, isValidLanguageKey, organizationLanguages]);

  useEffect(() => {
    const urlLanguageKey = getUrlLanguageKey();
    if (organizationLanguages.length > 0 && urlLanguageKey && !isValidLanguageKey(urlLanguageKey)) {
      const validLanguage = organizationLanguages[0];
      const newUrl = createLanguageKeyReplacedUrl(validLanguage);
      i18n.changeLanguage(validLanguage, () => {
        setLanguage(validLanguage);
        window.history.replaceState(null, "", newUrl);
      });
    }
  }, [organizationLanguages, getUrlLanguageKey, isValidLanguageKey, i18n, setLanguage, language]);

  const createHeaderUser = (user, customerData, originalMemberType) => {
    const id = parseFloat(user.getSiaId());
    if (user.isMemberTypeGroupOf("single")) {
      return {
        id: id,
        name: [customerData.firstname, customerData.lastname].join(" "),
        type: originalMemberType,
      };
    }
    if (user.isMemberTypeGroupOf("corporate")) {
      return {
        id: id,
        name: customerData.name,
        additionalName: customerData.additional_name,
        type: originalMemberType,
      };
    }
  };

  useEffect(() => {
    if (wasIDPInitializationStarted || organizationLanguages.length === 0) {
      return;
    }
    i18n.changeLanguage(getApplyingLanguage(), () => {
      const lang = isValidLanguageKey(i18n.language) ? i18n.language : organizationLanguages[0];
      setLanguage(lang);
      process.env.REACT_APP_CMS_URL && loadAliases(lang);
    });
    setIsLoaderShown(true); // todo, refactor loader handling SW-167
    setWasIDPInitializationStarted(true);
    keycloakIDPAdapter.init(async (idpUser) => {
      setIsIDPInitialized(true);
      setIsLoaderShown(false);
      if (idpUser) {
        if (localStorage.getItem("sia.webPlatform.forceUserLanguage")) {
          localStorage.removeItem("sia.webPlatform.forceUserLanguage");
          localStorage.setItem("sia.webPlatform.languageKey", idpUser.getLocale());
          const languageKeyReplacedUrl = createLanguageKeyReplacedUrl(idpUser.getLocale());
          window.location.replace(languageKeyReplacedUrl);
          return;
        }
        const servicesRoles = ["sia_role_member_corporate", "sia_role_member_single"];
        if (servicesRoles.includes(idpUser.role)) {
          setIsSubscribedToAnyService(true);
        }

        const data = await webPlatformAdapter.getLoginInformation(i18n.language);
        const originalMemberType = data[0].member_type.name;
        const memberType = memberTypeRepository.getById(data[0].member_type.id);
        const hasSubsidiaries = data[0].has_subsidiaries;
        const user = new User(idpUser.getUsername(), memberType, idpUser.getLocale(), idpUser.getRole(), originalMemberType, hasSubsidiaries);

        const dataCustomer = await webPlatformAdapter.getCustomerData(user);
        const customerData = dataCustomer[0];
        setHeaderUser(createHeaderUser(user, customerData, originalMemberType));
        setUser(user);
        handlePrivacyPolicyNotification();
      }
    });
    // eslint-disable-next-line
  }, [wasIDPInitializationStarted, i18n, getApplyingLanguage, translate, organizationLanguages]);

  const createLanguageKeyReplacedUrl = (languageKey) => {
    let pathParts = window.location.pathname.split("/");
    pathParts[1] = languageKey;
    const hash = window.location.hash ? window.location.hash : "";
    const search = window.location.search ? window.location.search : "";
    return window.location.origin + pathParts.join("/") + search + hash;
  };

  const renderPage = (component) => {
    const urlLanguageKey = getUrlLanguageKey();
    if (urlLanguageKey && !isValidLanguageKey(urlLanguageKey)) {
      return <NotFoundPage />;
    }
    return component;
  };

  useEffect(() => {
    if (language && organizationCode) {
      loadMenu(organizationCode);
    }
    // eslint-disable-next-line
  }, [language, organizationCode]);

  useEffect(() => {
    if (organizationCode && organizationLanguages?.length === 0) {
      fetchOrganizationData(organizationCode);
    }
    // eslint-disable-next-line
  }, [organizationCode, history.location.pathname]);

  const loadMenu = async (organizationCode) => {
    const data = await cmsAdapter.fetchMenuItemsByOrganizationV2(
      language,
      organizationCode,
      history
    );
    setMenuItems(data.menuItems);
    loadNoMenu();
  };

  async function fetchOrganizationData(organizationCode) {
    try {
      const lang = history.location.pathname.split("/")[1] || "de";
      const data = await cmsAdapter.fetchOrganizationData(organizationCode, lang);
      const languages = data?.language.map((lang) => lang.id);
      if (languages && languages.length > 0) {
        setOrganizationLanguages(languages);
      } else {
        setOrganizationLanguages(["de", "fr", "it", "en"]);
      }
      setOrganisationData(data);
    } catch (error) {
      setOrganizationLanguages(["de", "fr", "it", "en"]);
    }
  }

  async function loadNoMenu() {
    const data = await cmsAdapter.fetchOrgaNoMenu(
      language,
      organizationCode
    );
    setNoMenuPages(data);
    setIsLoadingPages(false);
  }

  async function loadAliases(lang) {
    const alias = history.location.pathname;
    const data = await cmsAdapter.fetchAliasesV2(lang, alias);
    const findAlias = data?.length > 0 && data[0];
    if (findAlias?.alias) {
      const usedLang = findAlias.lang || lang;
      if (usedLang !== lang) localStorage.setItem("sia.webPlatform.languageKey", usedLang);
      if (findAlias.destination) {
        window.location.href = findAlias.destination.startsWith("http") ? findAlias.destination : `/${usedLang}${findAlias.destination}`;
      } else {
        const to = `/${usedLang}/cms/node/${findAlias.node}${findAlias.section_element ? `#${findAlias.section_element}` : ""}`;
        window.location.replace(to);
      }
    }
  }

  const contactPage = organisationData?.contact_page || null;
  const newsletterPage = organisationData?.newsletter_page || null;
  const privacyPolicyPage = organisationData?.privacy_policy_page || null;
  const footerData = organisationData?.footer_data || {};

  const footerLinks = [
    ...(newsletterPage
      ? [
        {
          target: "/de/cms/node/" + newsletterPage,
          label: "layout.footer.links.newsletter",
        },
      ]
      : []),
    {
      target: privacyPolicyPage ? `/${language}/cms/${privacyPolicyPage}` : process.env.REACT_APP_WEB_PLATFORM_URL + `/${language}/cms/privacypolicy`,
      label: "layout.footer.links.privacy-policy.orga",
    },
  ];

  const contactUsTarget = contactPage ? `/${language}/cms/node/` + contactPage : `https://www.sia.ch/${language}/cms/node/52`;
  const contactUs = {
    title: "layout.footer.contact.contact-us",
    buttons: [
      {
        target: contactUsTarget,
        label: "layout.footer.contact.contact",
      },
    ],
  };
  const socialNetworks = organisationData.social_networks
    ? Object.fromEntries(organisationData.social_networks?.map((el) => [el.name?.toLowerCase(), el.url]))
    : {};
  const follow = {
    newsletterButton: footerContents.follow.newsletterButton,
    title: footerContents.follow.title,
    social: {
      ...socialNetworks,
    },
  };

  const showHeaderAndFooter = !window.location.href.includes("/maintenance");

  const setOrgaCodeFromParams = (jsonParameters, shouldChangeLanguage) => {
    const organizationCodeFromUrl = window.location.hostname.split(".")[0];
    const sectionFind = jsonParameters.sections.find(section => {
      return section.domains.find(domain => domain.domain === organizationCodeFromUrl)
    })

    if (shouldChangeLanguage && sectionFind) {
      const domain = sectionFind.domains.find(domain => domain.domain === organizationCodeFromUrl);
      const domainLang = domain?.lang;
      if (domainLang) {
        const newUrl = createLanguageKeyReplacedUrl(domainLang);
        i18n.changeLanguage(domainLang, () => {
          setLanguage(domainLang);
          window.history.replaceState(null, "", newUrl);
          window.location.reload()
        });
      }
    }
    setOrganizationCode(sectionFind?.code || organizationCodeFromUrl);
    setTimeout(() => {
      loadParameters(sectionFind?.code || organizationCodeFromUrl);
    }, 1000 * 60 * MINUTES_BETWEEN_PARAMETER_CHECKS);
  }

  const loadParameters = async (code, shouldChangeLanguage) => {
    const jsonParameters = await fetchParameters(process.env.REACT_APP_PARAMETERS_URL);
    if (process.env.REACT_APP_VERSION) setIsCorrectVersion(checkVersion(jsonParameters, process.env.REACT_APP_VERSION));
    setIsMaintenanceMode(checkMaintenancePage(jsonParameters));

    const jsonDisableFlags = getDisableFlags(jsonParameters);
    setDisableFlags(jsonDisableFlags);
    if (!code) setOrgaCodeFromParams(jsonParameters, shouldChangeLanguage);
    else {
      setTimeout(() => {
        loadParameters(code);
      }, 1000 * 60 * MINUTES_BETWEEN_PARAMETER_CHECKS);
    }
  };

  useEffect(() => {
    if (!process.env.REACT_APP_PARAMETERS_URL) {
      setOrganizationCode(window.location.hostname.split(".")[0]);
      return;
    }
    loadParameters(null, history.location.pathname === "/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <>
      <HelmetProvider>
        <Helmet>
          <html lang={i18n.language} />
        </Helmet>
      </HelmetProvider>
      {isLoaderShown && <Loader />}
      <Layout>
        <GlobalContext.Provider value={globalContextValue}>
          {showHeaderAndFooter && isCorrectVersion === false &&
            <WrongVersionModal translate={translate} />
          }
          {showHeaderAndFooter &&
            <HeaderClient
              pathFactory={pathFactory}
              history={history}
              user={user}
              menuItems={menuItems}
              i18n={i18n}
              createLanguageKeyReplacedUrl={createLanguageKeyReplacedUrl}
              translate={translate}
              headerUser={headerUser}
              organizationCode={organizationCode}
              defaultAppSwitcherValue={defaultAppSwitcherValue}
              availableLanguages={organizationLanguages}
              language={language}
              organisationId={organisationData?.footer_data?.id}
            />
          }
          <Body>
            <HttpErrorContextProvider>
              <WizardProvider>
                <Switch>
                  <Route exact path="/maintenance" component={() => <MaintenancePage translate={translate} isMaintenanceMode={isMaintenanceMode} />} />
                  {organizationLanguages.length && <Route exact path="/" component={() => <Redirect to={`/${getApplyingLanguage()}`} />} />}
                  {organizationLanguages.length && (
                    <Route exact path="/:languageKey" render={() => renderPage(<CMSContent nodeId={null} isOrgaHomePage />)} />
                  )}
                  {noMenuPages?.map((page) => {
                    return (
                      <Route key={page.id} exact path={`/:languageKey/cms${page.alias}`} render={() => renderPage(<CMSContent nodeId={page.id} />)} />
                    );
                  })}
                  {!isLoadingPages && <Route path="/:languageKey/cms/hash/:alias" render={() => renderPage(<CMSContent isHash />)} />}
                  {!isLoadingPages && <Route path="/:languageKey/cms/:alias" render={() => renderPage(<CMSContent />)} />}
                  {!isLoadingPages && <Route path="/:languageKey/search/:text" render={() => renderPage(<CMSSearch />)} />}
                  <Route path="/:languageKey/catalog" render={() => renderPage(<Catalog />)} />
                  <Route exact path="/:languageKey/agenda" render={() => renderPage(<Agenda />)} />
                  <Route exact path="/:languageKey/agenda/:id" render={() => renderPage(<AgendaAppPage isEventOccurrence={true} />)} />
                  <Route exact path="/:languageKey/agenda/speaker/:id" render={() => renderPage(<AgendaAppPage />)} />
                  <Route
                    path="/:languageKey/newsletter/unsubscribe/:hashed_key/:product_id"
                    render={() => renderPage(<UnsubscribeFromNewsletter />)}
                  />
                  <Route path="/:languageKey/error" component={ErrorPage} />
                  <Route path="*" component={isLoadingPages ? () => <></> : NotFoundPage} />
                </Switch>
              </WizardProvider>
            </HttpErrorContextProvider>
            <CookieClient history={history} lang={language} />
          </Body>
          {showHeaderAndFooter &&
            <Footer
              follow={follow}
              contactUs={contactUs}
              links={footerLinks}
              about={null}
              translate={translate}
              customAbout={
                <p>
                  {footerData &&
                    Array.from({ length: 9 }, (_, i) => footerData[`address_line_${i + 1}`])
                      .filter((el) => el)
                      .map((line, index) => (
                        <span key={index}>
                          {index > 0 && <br />}
                          {line}
                        </span>
                      ))}
                </p>
              }
            />
          }
        </GlobalContext.Provider>
      </Layout>
      <PrivacyPolicyComponent />
    </>
  );
}

export default App;
